import { useEffect } from 'react'

interface UseLoadScriptOptions {
  src: string
  onLoad?: () => void
  onError?: (event: Event | string) => void
  id?: string
  async?: boolean
  defer?: boolean
}

/**
 * A custom React hook to dynamically load an external JavaScript script.
 * @param options - Configuration for the script loading.
 * @param options.src - The URL of the script to load.
 * @param options.onLoad - Callback function to execute when the script loads successfully.
 * @param options.onError - Callback function to execute if the script fails to load.
 * @param options.id - An optional ID for the script element.
 * @param options.async - Whether the script should be loaded asynchronously (default: true).
 * @param options.defer - Whether the script should be deferred (default: true).
 */
export const useLoadScript = ({ src, onLoad, onError, id, async = true, defer = true }: UseLoadScriptOptions) => {
  useEffect(() => {
    // Check if the script already exists
    const existingScript = document.getElementById(id || src)

    if (existingScript) {
      if (onLoad) {
        // If the script already exists, call onLoad immediately
        // This ensures initialization logic runs even if script was loaded in a prior render.
        onLoad()
      }
      return // Exit if script already exists
    }

    const script = document.createElement('script')
    script.src = src
    script.async = async
    script.defer = defer
    script.id = id || src
    script.type = 'text/javascript'

    const handleLoad = () => {
      if (onLoad) {
        onLoad()
      }
    }

    const handleError = (event: Event | string) => {
      if (onError) {
        onError(event)
      }
    }

    script.addEventListener('load', handleLoad)
    script.addEventListener('error', handleError)

    document.body.appendChild(script)

    // Cleanup function
    return () => {
      script.removeEventListener('load', handleLoad)
      script.removeEventListener('error', handleError)
      // Only remove if this specific script element was added by this hook
      // This helps avoid issues if multiple components try to load the same script
      // or if another part of the app legitimately added it.
      if (document.body.contains(script) && script.id === (id || src)) {
        document.body.removeChild(script)
      }
    }
  }, [src, onLoad, onError, id, async, defer])
}
